import {
  QueryCache,
  MutationCache,
  QueryClient,
  type QueryKey,
} from '@tanstack/react-query';

/** Default query behavior: prefer fresh data over long-lived cache. */
export const DEFAULT_QUERY_OPTIONS = {
  /** Treat fetched data as stale immediately so invalidations and remounts refetch. */
  staleTime: 0,
  gcTime: 5 * 60_000,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
  refetchOnMount: true,
  retry: 1,
} as const;

export interface CreateAppQueryClientOptions {
  onQueryError?: (error: unknown, query: { queryKey: QueryKey }) => void;
  onMutationError?: (error: unknown) => void;
}

/**
 * Shared QueryClient for PeptideClients apps. Keeps cache behavior consistent
 * so writes that call `refreshQueries` show up without a full page reload.
 */
export function createAppQueryClient(options: CreateAppQueryClientOptions = {}): QueryClient {
  const { onQueryError, onMutationError } = options;
  return new QueryClient({
    defaultOptions: {
      queries: { ...DEFAULT_QUERY_OPTIONS },
    },
    queryCache: onQueryError
      ? new QueryCache({
          onError: (error, query) => {
            onQueryError(error, query);
          },
        })
      : undefined,
    mutationCache: onMutationError
      ? new MutationCache({
          onError: (error) => {
            onMutationError(error);
          },
        })
      : undefined,
  });
}

/**
 * Invalidate and refetch active queries for each key prefix. Await this in
 * mutation `onSuccess` so the UI updates before the success toast.
 */
export async function refreshQueries(
  qc: QueryClient,
  ...queryKeys: readonly QueryKey[]
): Promise<void> {
  await Promise.all(
    queryKeys.map((queryKey) =>
      qc.invalidateQueries({
        queryKey,
        refetchType: 'active',
      }),
    ),
  );
}
